import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import type { InstitutionProfile } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { Tooltip } from '@/components/ui/Tooltip';
import { useToast } from '@/components/ui/Toast';
import { Icon, type IconName } from '@/components/icons';
import {
  currencyOptions,
  languageOptions,
  timezoneOptions,
} from '@/lib/localisation';
import styles from './AdminProfilePage.module.css';

/** The restricted fields, which read as read-only and change by approval. */
const RESTRICTED_FIELDS = [
  { key: 'legalName', tooltipKey: 'legalName' },
  { key: 'registrationNumber', tooltipKey: 'registrationNumber' },
  { key: 'educationAuthorization', tooltipKey: 'educationAuthorization' },
  { key: 'institutionCode', tooltipKey: 'institutionCode' },
] as const satisfies readonly { key: keyof InstitutionProfile; tooltipKey: string }[];

type RestrictedFieldKey = (typeof RESTRICTED_FIELDS)[number]['key'];

/** A label with an information tooltip beside it, for the fields worth explaining. */
function FieldWithTip({
  id,
  label,
  tip,
  children,
}: {
  id: string;
  label: string;
  tip: string;
  children: React.ReactNode;
}) {
  return (
    <div className="ui-formField">
      <div className={styles.labelRow}>
        <label className="ui-formFieldLabel" htmlFor={id}>
          {label}
        </label>
        <Tooltip content={tip}>
          <span className={styles.labelTipIcon} aria-hidden="true">
            <Icon name="info" size={14} />
          </span>
        </Tooltip>
      </div>
      <div>{children}</div>
    </div>
  );
}

function ProfileSection({
  icon,
  title,
  description,
  dirty,
  dirtyText,
  extra,
  children,
}: {
  icon: IconName;
  title: string;
  description: string;
  dirty?: boolean;
  dirtyText?: string;
  extra?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className={styles.section}>
      <header className={styles.sectionHead}>
        <span className={styles.sectionIcon} aria-hidden="true">
          <Icon name={icon} size={18} />
        </span>
        <div className={styles.sectionHeadText}>
          <h2 className={styles.sectionTitle}>{title}</h2>
          <p className={styles.sectionDescription}>{description}</p>
        </div>
        {dirty && dirtyText && <span className={styles.sectionBadge}>{dirtyText}</span>}
        {extra}
      </header>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

export function AdminProfilePage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const { state, reload, replace } = useAsyncData<InstitutionProfile>(
    `admin-profile-${institutionId}`,
    () => api.admin.profile.get(institutionId),
    !!institutionId,
  );

  const [form, setForm] = useState<Partial<InstitutionProfile> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [changeRequestLoading, setChangeRequestLoading] = useState(false);

  const profile = state.status === 'ready' ? state.data : null;
  const currentForm = form ?? profile;
  const hasChanges = form !== null;

  const locale = i18n.language === 'fr' ? 'fr' : 'en';
  const currencies = useMemo(() => currencyOptions(locale), [locale]);
  const timezones = useMemo(() => timezoneOptions(locale), [locale]);
  const languages = useMemo(() => languageOptions(locale), [locale]);

  const changedCount = useMemo(() => {
    if (!form || !profile) return 0;
    return Object.keys(form).filter(
      (key) => String(form[key as keyof InstitutionProfile] ?? '') !== String(profile[key as keyof InstitutionProfile] ?? ''),
    ).length;
  }, [form, profile]);

  const tip = (key: string) => t(`admin.profile.tooltips.${key}`);

  if (state.status === 'loading' && !profile) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('admin.profile.loading')}</div>
      </div>
    );
  }

  if (state.status === 'error' && !profile) {
    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name="error" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.profile.title')}</h2>
          <p className={styles.emptyStateBody}>{t('common.error')}</p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      </div>
    );
  }

  if (!currentForm) {
    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name="profile" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.profile.title')}</h2>
          <p className={styles.emptyStateBody}>{t('common.noResults')}</p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      </div>
    );
  }

  const updateField = (field: keyof InstitutionProfile, value: string) => {
    setSaveSuccess(false);
    setForm((prev) => ({ ...(prev ?? currentForm), [field]: value }));
  };

  const handleSave = async () => {
    if (!form || !institutionId) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      const updated = await api.admin.profile.update(institutionId, form);
      replace(updated);
      setForm(null);
      setSaveSuccess(true);
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangeRequest = async () => {
    if (!institutionId) return;
    setChangeRequestLoading(true);
    try {
      const body: Record<string, unknown> = {};
      for (const { key } of RESTRICTED_FIELDS) {
        if (currentForm[key]) {
          body[key as string] = currentForm[key];
        }
      }
      await api.admin.profile.submitRestrictedChange(institutionId, body);
      toast.success(t('admin.profile.changeRequestSubmitted'));
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setChangeRequestLoading(false);
    }
  };

  const restrictedInput = (key: RestrictedFieldKey) => {
    return (
      <div className={styles.verifiedRow}>
        <input
          id={`profile-${key}`}
          className={`${styles.field} ${styles.fieldReadOnly}`}
          value={currentForm[key] ?? ''}
          disabled
        />
      <span className={styles.verifiedBadge}>
        <Icon name="checkmark" size={12} />
        {t('admin.profile.verifiedBadge')}
      </span>
    </div>
  );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.headerName}>{t('admin.profile.title')}</h1>
          <p className={styles.headerSubtitle}>{t('admin.profile.subtitle')}</p>
        </div>
      </header>

      {saveSuccess && (
        <div className={styles.successMessage}>{t('admin.profile.saved')}</div>
      )}

      <Card>
        <div className={styles.form}>
          <ProfileSection
            icon="institution"
            title={t('admin.profile.sections.identity')}
            description={t('admin.profile.sections.identityDescription')}
            extra={
              <Tooltip content={t('admin.profile.tooltips.changeRequest')}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleChangeRequest}
                  loading={changeRequestLoading}
                  disabled={changeRequestLoading || saving}
                >
                  {t('admin.profile.submitChangeRequest')}
                </Button>
              </Tooltip>
            }
          >
            <div className={styles.formRow}>
              <FieldWithTip id="profile-legalName" label={t('admin.profile.legalName')} tip={tip('legalName')}>
                {restrictedInput('legalName')}
              </FieldWithTip>
              <FieldWithTip id="profile-registrationNumber" label={t('admin.profile.registrationNumber')} tip={tip('registrationNumber')}>
                {restrictedInput('registrationNumber')}
              </FieldWithTip>
            </div>
            <div className={styles.formRow}>
              <FieldWithTip id="profile-educationAuthorization" label={t('admin.profile.educationAuthorization')} tip={tip('educationAuthorization')}>
                {restrictedInput('educationAuthorization')}
              </FieldWithTip>
              <FieldWithTip id="profile-institutionCode" label={t('admin.profile.institutionCode')} tip={tip('institutionCode')}>
                {restrictedInput('institutionCode')}
              </FieldWithTip>
            </div>
            <div className={styles.formRow}>
              <FieldWithTip id="profile-institutionType" label={t('admin.profile.institutionType')} tip={tip('institutionType')}>
                <input
                  id="profile-institutionType"
className={(styles.field as string) + ' ' + (styles.fieldReadOnly as string)}
                  value={
                    currentForm.institutionType
                      ? t(`admin.profile.institutionTypes.${currentForm.institutionType}`, {
                          defaultValue: currentForm.institutionType,
                        })
                      : t('admin.profile.institutionTypeUnset')
                  }
                  disabled
                />
              </FieldWithTip>
            </div>
          </ProfileSection>

          <ProfileSection
            icon="profile"
            title={t('admin.profile.sections.public')}
            description={t('admin.profile.sections.publicDescription')}
            dirty={form?.displayName !== undefined || form?.tradingName !== undefined || form?.acronym !== undefined}
            dirtyText={t('admin.profile.unsaved')}
          >
            <div className={styles.formRow}>
              <FieldWithTip id="displayName" label={t('admin.profile.displayName')} tip={tip('displayName')}>
                <input id="displayName" className={styles.field} placeholder={t('admin.profile.displayNamePlaceholder')} value={currentForm.displayName ?? ''} onChange={(e) => updateField('displayName', e.target.value)} />
              </FieldWithTip>
              <FieldWithTip id="tradingName" label={t('admin.profile.tradingName')} tip={tip('tradingName')}>
                <input id="tradingName" className={styles.field} placeholder={t('admin.profile.tradingNamePlaceholder')} value={currentForm.tradingName ?? ''} onChange={(e) => updateField('tradingName', e.target.value)} />
              </FieldWithTip>
            </div>
            <div className={styles.formRow}>
              <FieldWithTip id="acronym" label={t('admin.profile.acronym')} tip={tip('acronym')}>
                <input id="acronym" className={styles.field} placeholder={t('admin.profile.acronymPlaceholder')} value={currentForm.acronym ?? ''} onChange={(e) => updateField('acronym', e.target.value)} />
              </FieldWithTip>
            </div>
          </ProfileSection>

          <ProfileSection
            icon="mail"
            title={t('admin.profile.sections.contact')}
            description={t('admin.profile.sections.contactDescription')}
            dirty={form?.contactEmail !== undefined || form?.contactPhone !== undefined || form?.website !== undefined || form?.supportEmail !== undefined || form?.supportPhone !== undefined}
            dirtyText={t('admin.profile.unsaved')}
          >
            <div className={styles.formRow}>
              <FormField id="contactEmail" label={t('admin.profile.contactEmail')}>
                <input id="contactEmail" type="email" className={styles.field} placeholder={t('admin.profile.contactEmailPlaceholder')} value={currentForm.contactEmail ?? ''} onChange={(e) => updateField('contactEmail', e.target.value)} />
              </FormField>
              <FormField id="contactPhone" label={t('admin.profile.contactPhone')}>
                <input id="contactPhone" type="tel" className={styles.field} placeholder={t('admin.profile.contactPhonePlaceholder')} value={currentForm.contactPhone ?? ''} onChange={(e) => updateField('contactPhone', e.target.value)} />
              </FormField>
            </div>
            <div className={styles.formRow}>
              <FormField id="website" label={t('admin.profile.website')}>
                <input id="website" type="url" className={styles.field} placeholder={t('admin.profile.websitePlaceholder')} value={currentForm.website ?? ''} onChange={(e) => updateField('website', e.target.value)} />
              </FormField>
              <FormField id="supportEmail" label={t('admin.profile.supportEmail')}>
                <input id="supportEmail" type="email" className={styles.field} placeholder={t('admin.profile.supportEmailPlaceholder')} value={currentForm.supportEmail ?? ''} onChange={(e) => updateField('supportEmail', e.target.value)} />
              </FormField>
            </div>
            <div className={styles.formRow}>
              <FormField id="supportPhone" label={t('admin.profile.supportPhone')}>
                <input id="supportPhone" type="tel" className={styles.field} placeholder={t('admin.profile.supportPhonePlaceholder')} value={currentForm.supportPhone ?? ''} onChange={(e) => updateField('supportPhone', e.target.value)} />
              </FormField>
            </div>
          </ProfileSection>

          <ProfileSection
            icon="building"
            title={t('admin.profile.sections.location')}
            description={t('admin.profile.sections.locationDescription')}
            dirty={form?.operatingAddress !== undefined}
            dirtyText={t('admin.profile.unsaved')}
          >
            <FieldWithTip id="operatingAddress" label={t('admin.profile.operatingAddress')} tip={tip('operatingAddress')}>
              <textarea id="operatingAddress" className={`${styles.field} ${styles.textarea}`} placeholder={t('admin.profile.operatingAddressPlaceholder')} value={currentForm.operatingAddress ?? ''} onChange={(e) => updateField('operatingAddress', e.target.value)} />
            </FieldWithTip>
          </ProfileSection>

          <ProfileSection
            icon="globe"
            title={t('admin.profile.sections.localisation')}
            description={t('admin.profile.sections.localisationDescription')}
            dirty={form?.defaultLanguage !== undefined || form?.timezone !== undefined || form?.currency !== undefined}
            dirtyText={t('admin.profile.unsaved')}
          >
            <div className={styles.formRow}>
              <FieldWithTip id="defaultLanguage" label={t('admin.profile.defaultLanguage')} tip={tip('defaultLanguage')}>
                <Select
                  id="defaultLanguage"
                  className={styles.field}
                  value={currentForm.defaultLanguage ?? 'en'}
                  onChange={(value) => updateField('defaultLanguage', value)}
                  options={languages}
                  searchable
                  searchPlaceholder={t('admin.profile.languageSelectPlaceholder')}
                  placeholder={t('admin.profile.languageSelectPlaceholder')}
                />
              </FieldWithTip>
              <FieldWithTip id="timezone" label={t('admin.profile.timezone')} tip={tip('timezone')}>
                <Select
                  id="timezone"
                  className={styles.field}
                  value={currentForm.timezone ?? 'UTC'}
                  onChange={(value) => updateField('timezone', value)}
                  options={timezones}
                  searchable
                  searchPlaceholder={t('admin.profile.timezoneSelectPlaceholder')}
                  placeholder={t('admin.profile.timezoneSelectPlaceholder')}
                />
              </FieldWithTip>
            </div>
            <div className={styles.formRow}>
              <FieldWithTip id="currency" label={t('admin.profile.currency')} tip={tip('currency')}>
                <Select
                  id="currency"
                  className={styles.field}
                  value={currentForm.currency ?? 'XAF'}
                  onChange={(value) => updateField('currency', value)}
                  options={currencies}
                  searchable
                  searchPlaceholder={t('admin.profile.currencySelectPlaceholder')}
                  placeholder={t('admin.profile.currencySelectPlaceholder')}
                />
              </FieldWithTip>
            </div>
          </ProfileSection>

          <ProfileSection
            icon="info"
            title={t('admin.profile.sections.about')}
            description={t('admin.profile.sections.aboutDescription')}
            dirty={form?.description !== undefined}
            dirtyText={t('admin.profile.unsaved')}
          >
            <FormField id="description" label={t('admin.profile.description')}>
              <textarea id="description" className={`${styles.field} ${styles.textarea}`} placeholder={t('admin.profile.descriptionPlaceholder')} value={currentForm.description ?? ''} onChange={(e) => updateField('description', e.target.value)} />
            </FormField>
          </ProfileSection>

          <div className={styles.actions}>
            <Button onClick={handleSave} loading={saving} disabled={!hasChanges || saving}>
              {t('admin.profile.save')}
            </Button>
            {hasChanges && (
              <span className={styles.unsavedChip} role="status">
                {t('admin.profile.unsavedCount', { count: changedCount })}
              </span>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}